import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Flame, ArrowRight, ArrowLeft, TrendingDown, Wallet, Loader2, Sparkles, CheckCircle2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const randId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const OBJECTIVES = [
  { id: 'sair_dividas', label: 'Quero sair das dívidas e limpar meu nome', icon: TrendingDown },
  { id: 'organizar', label: 'Quero apenas organizar meus gastos', icon: Wallet },
];

const CHALLENGES = [
  { id: 'endividado', label: 'Estou endividado' },
  { id: 'nao_guardo', label: 'Não consigo guardar dinheiro' },
  { id: 'nao_sei', label: 'Não sei para onde meu dinheiro vai' },
  { id: 'organizar_melhor', label: 'Quero me organizar melhor' },
];

const DURATIONS = [
  { id: 'menos_3m', label: 'Menos de 3 meses' },
  { id: '3_12m', label: '3 a 12 meses' },
  { id: 'mais_1a', label: 'Mais de 1 ano' },
];

const NIVEL_STYLES = {
  alto: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', label: 'Atenção alta' },
  medio: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', label: 'Atenção moderada' },
  baixo: { color: 'text-[#00FF94]', bg: 'bg-[#00FF94]/10', border: 'border-[#00FF94]/30', label: 'Situação controlada' },
};

const OnboardingWizard = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [desafio, setDesafio] = useState(null);
  const [tempo, setTempo] = useState(null);
  const [objective, setObjective] = useState(null);
  const [income, setIncome] = useState('');
  const [debt, setDebt] = useState('');
  const [loading, setLoading] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);

  if (!open) return null;

  const handleSelectChallenge = (id) => {
    setDesafio(id);
    setTimeout(() => setStep(2), 220);
  };

  const handleSelectDuration = (id) => {
    setTempo(id);
    setTimeout(() => setStep(3), 220);
  };

  const handleSelectObjective = (id) => {
    setObjective(id);
    setTimeout(() => setStep(4), 220);
  };

  const fetchDiagnosis = async () => {
    setLoading(true);
    setDiagnosis(null);
    setStep(5);
    try {
      const res = await axios.post(`${API}/quick-diagnosis`, {
        monthly_income: parseFloat(income) || 0,
        total_debt: parseFloat(debt) || 0,
        objective,
      });
      setDiagnosis(res.data);
    } catch (e) {
      setDiagnosis({
        titulo: 'Vamos organizar isso juntos',
        diagnostico: 'Não conseguimos gerar o diagnóstico agora, mas você já pode começar a organizar suas finanças e descobrir para onde vai o seu dinheiro.',
        pontos: ['Registre seus gastos do mês', 'Acompanhe seu saldo disponível', 'Defina um limite mensal'],
        nivel: 'medio',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitInputs = () => {
    if (!income || parseFloat(income) <= 0) return;
    fetchDiagnosis();
  };

  const handleFinish = () => {
    const monthStr = new Date().toISOString().slice(0, 10).slice(0, 7);
    const incomeVal = parseFloat(income) || 0;
    const debtVal = parseFloat(debt) || 0;

    // 1. Renda -> renda fixa "Salário" no modo anônimo
    if (incomeVal > 0) {
      let existing = [];
      try { existing = JSON.parse(localStorage.getItem('evite_dividas_local_income') || '[]'); } catch (e) { existing = []; }
      const hasSalario = existing.some(i => i.name === 'Salário');
      if (!hasSalario) {
        existing.push({
          income_id: `local_inc_${randId()}`,
          name: 'Salário',
          amount: incomeVal,
          income_type: 'fixa',
          date: `${monthStr}-05`,
          recurrence_id: `rec_${randId()}`,
          created_at: new Date().toISOString(),
        });
        localStorage.setItem('evite_dividas_local_income', JSON.stringify(existing));
      }
    }

    // 2. Dívida total -> card no painel (inicial = total para calcular % quitado depois)
    localStorage.setItem('evite_dividas_debt', JSON.stringify({ inicial: debtVal, total: debtVal, updated_at: new Date().toISOString() }));

    // 3. Objetivo
    if (objective) localStorage.setItem('evite_dividas_objetivo', objective);

    // 3b. Respostas do teste (desafio + tempo)
    if (desafio) localStorage.setItem('evite_dividas_desafio', desafio);
    if (tempo) localStorage.setItem('evite_dividas_tempo', tempo);

    // 4. Streak / ofensiva ativa desde o primeiro segundo
    localStorage.setItem('evite_dividas_streak', JSON.stringify({ ofensiva_atual: 1, last_active: new Date().toISOString().slice(0, 10) }));

    if (onClose) onClose();
    navigate('/expenses');
  };

  const progress = step * 20;
  const nivel = diagnosis ? (NIVEL_STYLES[diagnosis.nivel] || NIVEL_STYLES.medio) : NIVEL_STYLES.medio;

  return (
    <div className="fixed inset-0 z-[100] bg-[#050505] flex flex-col items-center pt-10 sm:pt-12 px-4 overflow-y-auto" data-testid="onboarding-wizard">
      {/* Top bar: back + progress + close */}
      <div className="w-full max-w-md flex items-center gap-3 mb-10">
        <button
          onClick={() => (step > 1 ? setStep(step - 1) : onClose && onClose())}
          className="text-gray-400 hover:text-white transition-colors p-1"
          data-testid="wizard-back-btn"
          aria-label="Voltar"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 h-3 bg-[#27272A] rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-[#00FF94] rounded-full"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
        <button
          onClick={() => onClose && onClose()}
          className="text-gray-500 hover:text-white transition-colors p-1"
          data-testid="wizard-close-btn"
          aria-label="Fechar"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <AnimatePresence mode="wait">
        {/* STEP 1 - Maior desafio */}
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="w-full max-w-md flex flex-col items-center text-center"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF94]/10 text-[#00FF94] text-xs font-semibold uppercase tracking-wider mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Passo 1 de 5
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-8 leading-tight">
              Qual seu maior desafio hoje?
            </h2>
            <div className="w-full space-y-3">
              {CHALLENGES.map((opt, idx) => {
                const selected = desafio === opt.id;
                return (
                  <motion.button
                    key={opt.id}
                    onClick={() => handleSelectChallenge(opt.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    className={`w-full p-4 rounded-2xl cursor-pointer border-2 flex items-center gap-4 text-left transition-all group ${
                      selected ? 'border-[#00FF94] bg-[#00FF94]/10' : 'border-[#27272A] bg-[#121212] hover:border-[#00FF94]/60'
                    }`}
                    data-testid={`wizard-challenge-option${idx + 1}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected ? 'border-[#00FF94]' : 'border-gray-500'}`}>
                      {selected && <div className="w-2.5 h-2.5 rounded-full bg-[#00FF94]" />}
                    </div>
                    <span className="flex-1 font-semibold text-white">{opt.label}</span>
                    <ArrowRight className="w-5 h-5 text-gray-600 group-hover:text-[#00FF94] transition-colors" />
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* STEP 2 - Há quanto tempo */}
        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="w-full max-w-md flex flex-col items-center text-center"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF94]/10 text-[#00FF94] text-xs font-semibold uppercase tracking-wider mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Passo 2 de 5
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-8 leading-tight">
              Há quanto tempo isso acontece?
            </h2>
            <div className="w-full space-y-3">
              {DURATIONS.map((opt, idx) => {
                const selected = tempo === opt.id;
                return (
                  <motion.button
                    key={opt.id}
                    onClick={() => handleSelectDuration(opt.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    className={`w-full p-4 rounded-2xl cursor-pointer border-2 flex items-center gap-4 text-left transition-all group ${
                      selected ? 'border-[#00FF94] bg-[#00FF94]/10' : 'border-[#27272A] bg-[#121212] hover:border-[#00FF94]/60'
                    }`}
                    data-testid={`wizard-duration-option${idx + 1}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected ? 'border-[#00FF94]' : 'border-gray-500'}`}>
                      {selected && <div className="w-2.5 h-2.5 rounded-full bg-[#00FF94]" />}
                    </div>
                    <span className="flex-1 font-semibold text-white">{opt.label}</span>
                    <ArrowRight className="w-5 h-5 text-gray-600 group-hover:text-[#00FF94] transition-colors" />
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* STEP 3 - Objetivo */}
        {step === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="w-full max-w-md flex flex-col items-center text-center"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF94]/10 text-[#00FF94] text-xs font-semibold uppercase tracking-wider mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Passo 3 de 5
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-8 leading-tight">
              Qual o seu principal objetivo financeiro hoje?
            </h2>
            <div className="w-full space-y-4">
              {OBJECTIVES.map((opt, idx) => {
                const Icon = opt.icon;
                const selected = objective === opt.id;
                return (
                  <motion.button
                    key={opt.id}
                    onClick={() => handleSelectObjective(opt.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    className={`w-full p-5 rounded-2xl cursor-pointer border-2 flex items-center gap-4 text-left transition-all group ${
                      selected ? 'border-[#00FF94] bg-[#00FF94]/10' : 'border-[#27272A] bg-[#121212] hover:border-[#00FF94]/60'
                    }`}
                    data-testid={`wizard-step1-option${idx + 1}`}
                  >
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${selected ? 'bg-[#00FF94]/20' : 'bg-white/5'}`}>
                      <Icon className={`w-5 h-5 ${selected ? 'text-[#00FF94]' : 'text-gray-300'}`} />
                    </div>
                    <span className="flex-1 font-semibold text-white">{opt.label}</span>
                    <ArrowRight className="w-5 h-5 text-gray-600 group-hover:text-[#00FF94] transition-colors" />
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* STEP 4 - Inputs */}
        {step === 4 && (
          <motion.div
            key="step4"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="w-full max-w-md flex flex-col items-center text-center"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF94]/10 text-[#00FF94] text-xs font-semibold uppercase tracking-wider mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Passo 4 de 5
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-2">
              Conta rápida pra gente entender seu momento
            </h2>
            <p className="text-gray-400 mb-8 text-sm">Seus dados ficam só no seu dispositivo. Anônimo.</p>

            <div className="w-full text-left space-y-8">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Sua Renda Mensal (R$)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={income}
                  onChange={(e) => setIncome(e.target.value)}
                  placeholder="0"
                  autoFocus
                  className="w-full bg-transparent border-b-2 border-[#27272A] text-3xl text-white py-3 focus:outline-none focus:border-[#00FF94] transition-colors placeholder:text-[#3F3F46] font-mono"
                  data-testid="wizard-step2-income-input"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Total de Dívidas Atuais (R$)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={debt}
                  onChange={(e) => setDebt(e.target.value)}
                  placeholder="0"
                  className="w-full bg-transparent border-b-2 border-[#27272A] text-3xl text-white py-3 focus:outline-none focus:border-[#00FF94] transition-colors placeholder:text-[#3F3F46] font-mono"
                  data-testid="wizard-step2-debt-input"
                />
              </div>
            </div>

            <motion.button
              onClick={handleSubmitInputs}
              disabled={!income || parseFloat(income) <= 0}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="w-full mt-12 bg-[#00FF94] text-black font-bold py-4 rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#33FFAD] transition-all flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(0,255,148,0.25)]"
              data-testid="wizard-step2-submit"
            >
              <Sparkles className="w-5 h-5" /> Gerar Meu Diagnóstico Inteligente
            </motion.button>
          </motion.div>
        )}

        {/* STEP 5 - Diagnóstico */}
        {step === 5 && (
          <motion.div
            key="step5"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="w-full max-w-md flex flex-col items-center text-center pb-12"
          >
            {loading ? (
              <div className="flex flex-col items-center py-16" data-testid="wizard-loading">
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-[#00FF94]/30 rounded-full blur-xl animate-pulse" />
                  <div className="relative w-16 h-16 rounded-2xl bg-[#0A0A0A] border border-[#00FF94]/40 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
                  </div>
                </div>
                <p className="text-white font-semibold text-lg">Analisando seu perfil financeiro...</p>
                <p className="text-gray-500 text-sm mt-1">Nossa IA está montando seu diagnóstico</p>
              </div>
            ) : diagnosis ? (
              <>
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${nivel.bg} ${nivel.color} text-xs font-semibold uppercase tracking-wider mb-5`}>
                  <Sparkles className="w-3.5 h-3.5" /> Seu diagnóstico • {nivel.label}
                </span>
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  className={`w-full bg-[#121212] border ${nivel.border} rounded-2xl p-6 sm:p-8 text-left relative overflow-hidden`}
                  data-testid="wizard-step3-result"
                >
                  <div className={`absolute top-0 left-0 right-0 h-1 ${nivel.color.replace('text-', 'bg-')}`} />
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 leading-tight">{diagnosis.titulo}</h3>
                  <p className="text-gray-300 leading-relaxed mb-5">{diagnosis.diagnostico}</p>
                  {Array.isArray(diagnosis.pontos) && diagnosis.pontos.length > 0 && (
                    <div className="space-y-2.5">
                      {diagnosis.pontos.map((p, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                          <CheckCircle2 className="w-5 h-5 text-[#00FF94] flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-300">{p}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>

                <div className="flex items-center gap-2 mt-6 text-[#FF9500] font-semibold text-sm bg-[#1A1A1A] border border-[#333] px-4 py-2 rounded-full">
                  <Flame className="w-4 h-4 fill-[#FF9500]" /> Sua ofensiva começa agora!
                </div>

                <motion.button
                  onClick={handleFinish}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full mt-6 bg-[#00FF94] text-black font-bold py-4 rounded-full hover:bg-[#33FFAD] transition-all flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(0,255,148,0.3)]"
                  data-testid="wizard-step3-result-cta"
                >
                  <Wallet className="w-5 h-5" /> Acessar Meu Organizador de Despesas
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OnboardingWizard;
